import React, {useEffect, useContext} from 'react'
import ReactModal from 'react-modal'
import {default as range} from "lodash/range"
import {classNames, keyActivationHandler} from 'js/studios/shared/utils'
import {CheckMarkCircledIcon as OldCheckMarkCircledIcon, CancelCircledIcon} from 'js/studios/shared/svg_icons'
import {trackEventSP} from 'js/studios/shared/tracking'
import {FlowFramesCtx} from "js/studios/shared/unredux/contexts/flow"
import {useOnMount} from "js/studios/shared/unredux/hooks"
import LogoWordmarkSvg from "images/brand2025/logo-wordmark.svg"
import InfoSvg from "images/brand2025/info.svg"

export const NON_ENGAGEMENT_BUDGET_OPTIONS = range(50, 2500, 50)
export const ENGAGEMENT_BUDGET_OPTIONS = range(100, 2500, 100).concat(range(2500, 10000, 500)).concat(range(10000, 25000, 1000))

export const COMMON_GEM_SHAPES = [
  {
    name: "round",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/round.png`,
    label: "Round"
  },
  {
    name: "princess",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/princess.png`,
    label: "Princess"
  },
  {
    name: "oval",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/oval.png`,
    label: "Oval"
  },
  {
    name: "emerald",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/emerald.png`,
    label: "Emerald"
  },
  {
    name: "pear",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/pear.png`,
    label: "Pear"
  },
  {
    name: "elongated_cushion",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/elongated-cushion.png`,
    label: "Elongated Cushion"
  },
  {
    name: "cushion",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/square-cushion.png`,
    label: "Square Cushion"
  },
  {
    name: "marquise",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/marquise.png`,
    label: "Marquise"
  },
  {
    name: "heart",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/heart.png`,
    label: "Heart"
  },
  {
    name: "radiant",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/radiant.png`,
    label: "Radiant"
  },
  {
    name: "asscher",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/asscher.png`,
    label: "Asscher"
  }

]
export const UNCOMMON_GEM_SHAPES = [
  {
    name: "rose",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/rose.png`,
    label: "Rose"
  },
  {
    name: "kite",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/kite.png`,
    label: "Kite"
  },
  {
    name: "hexagon",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/hexagon.png`,
    label: "Hexagon"
  },
  {
    name: "trillion",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/trillion.png`,
    label: "Trillion"
  },
  {
    name: "calf_coffin",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/calf-coffin.png`,
    label: "Calf / Coffin"
  },
  {
    name: "old_mine",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/old-mine.png`,
    label: "Old Mine"
  },
  {
    name: "old_euro",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/old-euro.png`,
    label: "Old Euro"
  },
  {
    name: "half_moon",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/half-moon.png`,
    label: "Half Moon"
  },
  {
    name: "shield",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/shield.png`,
    label: "Shield"
  },
  {
    name: "other",
    img: `${window.cm.structureUrl}/static/images/brand2025/gem-shapes/other.png`,
    label: "Other"
  }
]

export function NextBtn({children}) {
  return <div className="btn-group anim--fade-in mt--lg">
    <button className="btn btn--lg btn--wide btn--arrow-right">{children || "Next"}</button>
  </div>
}

export const FrameHeader = ({children, h2ClassName}) => {
  return <div className="flow-frame__header">
    <PreviousLink />
    <h2 className={classNames("txt--center", h2ClassName)}>{children}</h2>
  </div>
}

export const Frame = ({dark=false, cta="Next", canSubmit, onSubmit, className, children}) => {
  // for iOS, tweaking --page-bg to burgundy looks nicer as it gets status/address bars to match page
  useEffect(() => {
    if (dark) {
      document.body.style.setProperty("--page-bg", "var(--burgundy)")
      return () => document.body.style.removeProperty("--page-bg")
    }
  }, [dark])

  return <form
    className={classNames("flow-frame anim--fade-in", {"flow-frame--dark": dark}, className)}
    onSubmit={onSubmit}
    >
    <div className="flow-frame__body">
      {children}
      {canSubmit && cta !== false && <NextBtn>{cta || "Next"}</NextBtn>}
    </div>
  </form>
}


export function CheckMarkCircledIcon() {
  if (!window.cm.isRebrand) {
    return <OldCheckMarkCircledIcon />
  }

  return <div className="icon icon--check icon--matte"></div>
}

export function PreviousLink({onClick}) {
  const {gotoPrevFrame} = useContext(FlowFramesCtx) || {}

  // TODO remove legacy version once rebrand is turned on
  if (!window.cm.isRebrand) {
    return <a
      className="previous"
      tabIndex="0"
      aria-label="Go back"
      onClick={onClick}
      onKeyDown={keyActivationHandler(onClick)}>
      <i className="material-icons">chevron_left</i>
    </a>
  }

  if (!onClick) {
    onClick = gotoPrevFrame
  }

  const innerOnClick = ev => {
    ev.preventDefault()
    onClick(ev)
  }

  return <a
    className="previous"
    href="#"
    tabIndex="0"
    aria-label="Go back"
    onClick={innerOnClick}
    onKeyDown={keyActivationHandler(innerOnClick)}>
    <span className="icon icon--arrow-left"></span>
  </a>
}

// DEPRECATED, don't use with rebrand UI, just use a <select>
export class Select extends React.Component {
  render(){
    const selectId = this.props.id || `select-${Math.random()}`
    const selectAttrs = {...this.props, id: selectId}
    delete selectAttrs.label
    delete selectAttrs.insertLabelOption
    const insertLabelOption = this.props.insertLabelOption === undefined ? true : this.props.insertLabelOption

    return (
      <div className={"new-brand-select-wrapper select-wrapper" + (this.props.disabled ? " disabled" : "")}>
        {this.props.label && <label htmlFor={selectId} hidden>{this.props.label}</label>}
        <select {...selectAttrs}>
          {this.props.label && insertLabelOption && <option value="">-- {this.props.label} --</option>}
          {this.props.children}
        </select>
      </div>
    )
  }
}

// display a choice for an onboarding question with several varieties
export function CheckMarkCard({disabled, faded, className, checked, onClick, toggleChecked, imgUrl, imgs, value, label, description, children}) {
  const innerOnClick = (ev) => {
    ev.preventDefault()
    if (disabled) { return }
    if (onClick) {
      // TODO - once rebrand usage is on & legacy usage is removed this can be simplified
      const evChecked = window.cm.isRebrand ? !checked : checked
      onClick({preventDefault: () => {}, target: {checked: evChecked, value: value}})
    } else {
      // TODO deprecated, remove as legacy when all use is changed to onClick
      toggleChecked(ev)
    }
  }

  // TODO remove legacy version after rebrand turned on
  if (!window.cm.isRebrand) {
    const cssClass = classNames('card', className, {'disabled': disabled, 'faded': faded})
    return <div
      className={cssClass}
      role="button"
      aria-pressed={checked ? "true" : "false"}
      tabIndex="0"
      onClick={innerOnClick}
      onKeyDown={keyActivationHandler(innerOnClick)}
      >
      {imgUrl && <img src={imgUrl} />}
      {imgs && <div className="images">
        {imgs.map(imgUrl => <img key={imgUrl} src={imgUrl} />)}
      </div>}
      {label && <div className="card-label" title={label}>{label}</div>}
      {description && <p className="card-description">{description}</p>}
      {children}
      {checked && <div className="card-checked-wrapper">
        <CheckMarkCircledIcon />
      </div>}
    </div>
  }

  if (imgUrl) { throw new Error("new usage of CheckMarkCard should use imgs instead of imgUrl") }

  return <div
    className={classNames("check-mark-card", className, {"check-mark-card--faded": faded})}
    role="button"
    aria-pressed={checked ? "true" : "false"}
    tabIndex="0"
    onClick={innerOnClick}
    onKeyDown={keyActivationHandler(innerOnClick)}
    data-faded={faded || undefined}
    data-disabled={disabled || undefined}
    >
    {imgs && <div className="check-mark-card__imgs">
      {imgs.map(img => <img key={img} src={img} role="presentation" alt="" />)}
    </div>}
    {label && <div className="check-mark-card__label"><b>{label}</b></div>}
    {description && <div className="check-mark-card__description txt--body-2">{description}</div>}
    {children}
    <CheckMarkCircledIcon />
  </div>
}

export class HelpModal extends React.Component {
  componentDidUpdate(prevProps, _prevState) {
    if (prevProps.isOpen !== this.props.isOpen) {
      const action = this.props.isOpen ? 'action_open_help' : 'action_close_help'
      trackEventSP(action, 'consultation', {name: this.props.name})
    }
  }

  componentDidMount() {
    ReactModal.setAppElement('body')
  }

  // TODO remove after everything is rebrand
  legacyRender() {
    return <ReactModal
      isOpen={this.props.isOpen}
      overlayClassName={"help-modal-overlay" + (this.props.isOpen ? " open" : "")}
      className="help-modal"
      onRequestClose={this.props.onClose}
      closeTimeoutMS={300}
      contentLabel="help-modal"
    >
      <div>
        {this.props.children}
        <a className="help-modal-close" aria-label="Close help modal" tabIndex="0" onClick={this.props.onClose} onKeyDown={keyActivationHandler(this.props.onClose)}>
          <CancelCircledIcon />
        </a>
      </div>
    </ReactModal>
  }

  handleClose = ev => {
    ev.preventDefault()
    ev.stopPropagation()
    this.props.onClose(ev)
  }

  render() {
    if (!window.cm.isRebrand) { return this.legacyRender() }

    return <ReactModal
      isOpen={this.props.isOpen}
      overlayClassName="" // prevents default `style=` attrs from react-modal which would override our css
      className="ReactModal__content--cm ReactModal__content--padded"
      style={{overlay: {backgroundColor: undefined}}}
      onRequestClose={this.props.onClose}
      closeTimeoutMS={120}
      contentLabel="help-modal"
    >
      {this.props.children}
      <button className="ReactModal__close btn btn--icon btn--sm" href="#" aria-label="Close help modal" tabIndex="0" onClick={this.handleClose} onKeyDown={keyActivationHandler(this.handleClose)}>
        <span className="icon icon--x"></span>
      </button>
    </ReactModal>
  }
}

export function HelpLink({label, onClick}) {
  // TODO cleanup when all usage is on rebrand
  if (!window.cm.isRebrand) {
    <a className="help material-icons" aria-label="Show help" tabIndex="0" onClick={onClick} onKeyDown={keyActivationHandler(onClick)}>info_outlined</a>
  }

  const innerOnClick = ev => {
    ev.preventDefault()
    ev.stopPropagation()
    onClick(ev)
  }

  return <a
    className="help" aria-label={label || "Show help"} tabIndex="0" href="#"
    onClick={innerOnClick} onKeyDown={keyActivationHandler(innerOnClick)}>
    <InfoSvg />
  </a>
}

export function Splash({className, splashClassName, children}) {
  // TODO cleanup when all usage is on rebrand, cleanup eslint-disable below as well
  if (!window.cm.isRebrand) {
    return <div className={classNames("splash-wrapper", className)}>
      <div className="flow-splash fade-in">
        <div className="main-contents">
          <div className="logo-container">
            <a href="/">
              <img src="//images.custommade.com/90zzp4uzgC3IcNXn6rORdtyip3k=/custommade-attachments/13d550c8ed01060_intro_logo.png" alt="CustomMade logo" />
            </a>
          </div>
          <div className="frame">{children}</div>
        </div>
        <div className="mobile-logo-container">
          <img src="//images.custommade.com/l5NhIDjTAVvB3ABnr1fMWO6XNwc=/custommade-attachments/cf711a124360d14_glyph_primary.png" />
        </div>
        <div className={classNames("splash-image", splashClassName)}></div>
      </div>
    </div>
  }

  // for iOS, tweaking --page-bg to burgundy looks nicer as it gets status/address bars to match page
  // eslint-disable-next-line react-hooks/rules-of-hooks -- not actually conditional, see above
  useOnMount(() => {
    document.body.style.setProperty("--page-bg", "var(--burgundy)")
    return () => document.body.style.removeProperty("--page-bg")
  })

  return <div className={classNames("splash", className)}>
    <div className="splash__main anim--fade-in"><div className="splash__content">{children}</div></div>
    <div className="splash__logo anim--fade-in">
      <a href="/" aria-label="CustomMade homepage"><LogoWordmarkSvg /></a>
    </div>
    <div className={classNames("splash__hero anim--fade-in", splashClassName)}></div>
  </div>
}
